import { Newspaper } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useCurrentWorkspace } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { collectionInputKey, useCollectionInput } from "./collectionInput";

export function CollectionInputCard({
  collectionId,
  canManage,
}: {
  collectionId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const workspace = useCurrentWorkspace();
  const client = useQueryClient();
  const input = useCollectionInput(collectionId);
  const connect = useMutation({
    mutationFn: () => api.research.post("channels.collections.connect", { collectionId }),
    onSuccess: () => client.invalidateQueries({ queryKey: collectionInputKey(collectionId) }),
  });
  if (input.isLoading || input.isError) return null;
  if (!input.data)
    return canManage ? (
      <div>
        <Button
          variant="ghost"
          icon={Newspaper}
          disabled={connect.isPending}
          onClick={() => connect.mutate()}
        >
          {t("Connect news input")}
        </Button>
        {connect.isError ? (
          <p role="alert">
            {t("The news input could not be connected. Check access to the source workspace.")}
          </p>
        ) : null}
      </div>
    ) : null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-divider bg-bg-secondary p-4">
      <div>
        <p className="font-medium">{t("News input")}</p>
        <p className="text-small text-text-secondary">
          {t("{{count}} articles available · {{deposited}} kept in this collection", {
            count: input.data.total,
            deposited: input.data.deposited,
          })}
        </p>
        {input.data.error ? (
          <p role="alert" className="text-small">
            {input.data.error}
          </p>
        ) : null}
      </div>
      <Link
        to={"/knowledge/visuals" as never}
        search={{ audience: "workspace", collection: collectionId, ws: workspace.data?.id } as never}
        className="text-accent underline"
      >
        {t("Open in Knowledge gallery")}
      </Link>
      <Link to="/collections/$collectionId/news" params={{ collectionId }} search={{ ws: workspace.data?.id }}>{t("News input")}</Link>
    </div>
  );
}
